import { NEW_FEATURE } from "../constants";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const NewFeature = () => {
  const { t, i18n } = useTranslation();

  const stepVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.6,
        ease: "easeOut",
      },
    }),
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const currentLanguage =
    i18n.resolvedLanguage || i18n.language || i18n.options?.fallbackLng?.[0] || "en";
  const normalizedLanguage = String(currentLanguage).toLowerCase();
  const featureImage =
    normalizedLanguage.startsWith("de") && NEW_FEATURE.featureImg_DE
      ? NEW_FEATURE.featureImg_DE
      : NEW_FEATURE.featureImg_EN;
  const currentLanguageFeatures = i18n.getResource(
    currentLanguage,
    "translation",
    "newFeature.features"
  );
  const englishFeatures = i18n.getResource("en", "translation", "newFeature.features");

  const localizedFeatures = (
    Array.isArray(currentLanguageFeatures) && currentLanguageFeatures.length > 0
      ? currentLanguageFeatures
      : Array.isArray(englishFeatures) && englishFeatures.length > 0
      ? englishFeatures
      : NEW_FEATURE.features
  ).filter(
    (feature) =>
      feature &&
      typeof feature.title === "string" &&
      feature.title.trim() !== "" &&
      Array.isArray(feature.description) &&
      feature.description.length > 0
  );

  return (
    <section id="new_feature">
      <div className="max-w-7xl mx-auto px-4 border-t border-neutral-800 mb-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-12 "
        >
          <h2 className="text-3xl lg:text-6xl mt-20 tracking-tighter text-neutral-600 bg-clip-text ">
            {t("newFeature.sectionTitle")}
          </h2>
          <p className="mt-4 text-[16px] font-medium text-StandardSEPM max-w-xl mx-auto">
            {t("newFeature.sectionDescription")}
          </p>
        </motion.div>
        <p className="mt-4 text-[14px] font-medium text-neutral-800">
          {t("newFeature.introLine1")}
          <br />
          {t("newFeature.introLine2")}
        </p>

        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.2,
              },
            },
          }}
          className="flex flex-wrap md:flex-nowrap items-start gap-6"
        >
          {/* Left Section: Features */}
          <motion.div className="flex flex-col space-y-6 w-full md:w-1/2 mt-8">
            {localizedFeatures.map((feature, index) => {
              const isHighlighted = index === 0;

              return (
                <motion.div
                  custom={index}
                  variants={stepVariants}
                  key={index}
                  className={`flex flex-col space-y-1 ${
                    isHighlighted
                      ? "rounded-lg border-l-4 border-StandardSEPM px-3 py-3 animate-softPulse"
                      : ""
                  }`}
                >
                  {/* Feature Heading with Icon */}
                  <div className="flex items-start">
                    <div className="text-lg flex-shrink-0">
                      🚀 {/* Feature Icon */}
                    </div>
                    <div className="ml-4">
                      <p className="font-semibold text-StandardSEPM">
                        {feature.title}
                      </p>
                    </div>
                  </div>

                  {/* Feature Pointers */}
                  <ul className="ml-12 list-disc space-y-3">
                    {feature.description.map((pointer, idx) => (
                      <li key={idx} className="text-neutral-700 font-medium">
                        {pointer}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Right Section: Image */}
          <motion.div
            variants={fadeInUp}
            className="w-full md:w-1/2 flex justify-center items-center"
          >
            <motion.img
              src={featureImage?.src}
              alt={featureImage?.alt || t("newFeature.imageAlt")}
              className="max-w-full h-auto object-contain"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default NewFeature;
